import { useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import {
  setModal as doSetModal,
  setNewTimersSentEmailByEmail,
  deleteTimerSentByEmail as doDeleteTimerSentByEmail,
  showError,
} from '@store/common/actions';
import {
  zeroTime,
  getMomentToZero,
  getTimeElapsedToDate,
  getTimeElapsedDefault,
} from '@helpers/sendEmail';
import { useTranslation } from 'react-i18next';

const useCommon = (recoverEmail) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const config = useSelector((state) => state.config);
  const timeLimit = config?.send_email?.time_limit || 120;
  const typeOfTimeLimit = config?.send_email?.type_of_time_limit || 'seconds';
  const { modal, timersSentEmail = [] } = useSelector((state) => state.common);

  const findEmail = useCallback(
    (email) =>
      timersSentEmail
        .filter(
          (timerEmail) =>
            getTimeElapsedToDate(timerEmail.timeSent, { timeLimit, typeOfTimeLimit }) > 0
        )
        .find((timerSentEmail) => timerSentEmail.email === email),
    [timersSentEmail, timeLimit, typeOfTimeLimit]
  );

  const [timer, setTimer] = useState(() => {
    if (!recoverEmail) return zeroTime;

    const timerSentEmail = findEmail(recoverEmail);
    if (!timerSentEmail) return zeroTime;

    return getMomentToZero(
      getTimeElapsedToDate(timerSentEmail.timeSent, { timeLimit, typeOfTimeLimit })
    );
  });

  const isTimeOut = useMemo(() => timer?.toDate()?.getTime() <= 0, [timer]);

  const pushNewSentEmail = useCallback(
    ({ email, token }) => {
      if (!findEmail(email)) {
        dispatch(setNewTimersSentEmailByEmail({ email, token }));
        setTimer(getTimeElapsedDefault({ timeLimit, typeOfTimeLimit }));
      }
    },
    [dispatch, setTimer, findEmail]
  );

  const getIdIntervalTimer = useCallback(
    () =>
      setInterval(() => {
        setTimer((prev) => {
          const newTimer = moment(prev).add(-1, 'seconds');
          return newTimer?.toDate()?.getTime() <= 0 ? zeroTime : newTimer;
        });
      }, 1000),
    [setTimer]
  );

  const deleteTimerSentByEmail = useCallback(
    (email) => {
      dispatch(doDeleteTimerSentByEmail(email));
      dispatch(
        showError({
          title: t('login.resetPassword.common.errorMsg'),
          margin: '140px 24px 0px 24px',
        })
      );
      setTimer(zeroTime);
    },
    [dispatch]
  );

  const setModal = useCallback((data) => dispatch(doSetModal({ open: true, ...data })), [dispatch]);
  const resetModal = useCallback(() => dispatch(doSetModal({})), [dispatch]);

  return {
    modal,
    timer,
    isTimeOut,
    setModal,
    resetModal,
    getIdIntervalTimer,
    findEmail,
    pushNewSentEmail,
    deleteTimerSentByEmail,
  };
};

export default useCommon;
